import { Spring, clamp, dampTo } from './mech-pose';
/* ============================================================
   待機層（機庫／開始畫面的展示動畫）
   ============================================================

   機庫原本是「鏡頭在動、機體不動」—— 八個彈簧全部收斂到靜止值之後，
   畫面上唯一會動的只有核心發光的那條 sin。近距離看一台四公尺的機體
   一動不動，讀起來不是「待命」而是「模型檢視器裡的一顆 mesh」。

   ## 四個層，各自的理由

   1. **呼吸**（髖上下 ±2.4cm）。腳位是 IK 的輸入、髖高是它的參數，
      所以髖一沉膝就自己彎 —— 腳掌完全不動，不會有滑步。
      兩個不成整數比的週期疊加（3.6s / 5.3s），合成波幾十秒內不重複；
      單一正弦看得出節拍，那正是「機械上下」的來源。
   2. **重心轉移**（週期 9.4s 的軀幹 roll + 骨盆反向）。呼吸是縱向的，
      只有它會讓人覺得機體在「上下抽動」；橫向這一層讓它變成有重量的站姿。
   3. **頭部掃視**。最便宜也最有效的一項：伺服彈簧刻意欠阻尼（d < 2√k），
      轉過去會小小過衝再回來，那是機械頭部而不是生物頸部的動法。
      三段裡有一段強制回正 —— 一直亂看像在找東西，不像待命。
   4. **節拍事件**（每 4.6 秒一次，交替）。偶數拍排氣（背包掀開＋噴煙＋發光），
      奇數拍伺服校正（懸吊與後座各一個小 kick）。週期性的小事件讓
      「靜止的十秒」不存在，而它們全部借用既有的彈簧與排氣管線。

   **全部是 `t` 的決定性函數**，沒有 Math.random —— 展示畫面不進模擬，
   但截圖比對要能重現（`tools/mech-angles.js` 的角度標籤靠這個才對得上）。 */

var TAU = Math.PI * 2;

/** 唯一的調參面。振幅是以「機體全高 4.4」的原型單位計。 */
export var IDLE = {
  breathT: 3.6, swayT: 5.3, weightT: 9.4,
  breath: 0.024,      // 髖的上下位移
  torso: 0.016,       // 吸氣時胸口微仰
  roll: 0.011,        // 重心轉移的軀幹傾斜
  pelvis: 0.020,      // 骨盆反向，髖線與肩線的夾角跟著呼吸微微變化
  arm: 0.030,         // 右臂跟著呼吸沉浮（慢半拍靠彈簧以外的相位差做）
  scanYaw: 0.40, scanPitch: 0.09, scanEvery: 2.7,
  beat: 4.6
};

/** 決定性亂數：只吃整數段號，同一段永遠回同一個值。 */
function h(n) { var s = Math.sin(n * 127.1) * 43758.5453; return s - Math.floor(s); }

function newIdle() {
  return {
    k: 0, t: 0, seg: -1, beat: -1, yaw: 0, pitch: 0,
    yawS: new (Spring as any)(46, 10),      // d=10 < 2√46≈13.6 → 欠阻尼，servo 的過衝
    pitchS: new (Spring as any)(38, 10),
    o: { y: 0, pitch: 0, roll: 0, pelvis: 0, headYaw: 0, headPitch: 0, arm: 0 }
  };
}

/** 每幀。`on` = 1 進展示模式、0 退出（淡入淡出，不會跳）。

    回傳的偏移物件是**重用的同一顆**（每幀配置在這裡沒有意義）。
    另外它會直接推 `A` 的彈簧與排氣計時 —— 節拍事件本來就該走既有管線。 */
export function updateIdle(A, dt, on) {
  var I = A.idleL || (A.idleL = newIdle()), o = I.o;
  I.k = dampTo(I.k, on, on > 0.5 ? 2.4 : 6, dt);
  /* 走路／滑行／倒地一律讓位：待機層是「站著沒事做」的表演，
     疊在移動上只會變成雜訊。 */
  var k = I.k * (1 - A.dead) * clamp(1 - A.speedS * 3 - A.glide * 3, 0, 1);
  if (k < 0.001) {
    o.y = o.pitch = o.roll = o.pelvis = o.headYaw = o.headPitch = o.arm = 0;
    return o;
  }
  I.t += dt;
  var t = I.t;
  var br = Math.sin(t * TAU / IDLE.breathT) * 0.62 + Math.sin(t * TAU / IDLE.swayT) * 0.38;
  var sw = Math.sin(t * TAU / IDLE.weightT);

  /* --- 頭部掃視 --- */
  var seg = Math.floor(t / IDLE.scanEvery);
  if (seg !== I.seg) {
    I.seg = seg;
    I.yaw = h(seg) < 0.34 ? 0 : (h(seg + 91.7) - 0.5) * 2 * IDLE.scanYaw;
    I.pitch = (h(seg + 17.3) - 0.5) * 2 * IDLE.scanPitch;
  }
  o.headYaw = I.yawS.to(I.yaw, dt) * k;
  o.headPitch = I.pitchS.to(I.pitch, dt) * k;

  /* --- 節拍事件 --- */
  var beat = Math.floor(t / IDLE.beat);
  if (beat !== I.beat) {
    var first = I.beat < 0; I.beat = beat;
    if (!first && k > 0.5) {
      if (beat % 2 === 0) A.ventT = Math.max(A.ventT, 0.42);
      else { A.susp.kick(1.4); A.recoil.kick(2.4); }   // 伺服重新校正：小到只讀得出「動了一下」
    }
  }

  o.y = br * IDLE.breath * k;
  o.pitch = -br * IDLE.torso * k;
  o.roll = sw * IDLE.roll * k;
  o.pelvis = -sw * IDLE.pelvis * k;
  o.arm = Math.sin(t * TAU / IDLE.breathT - 0.9) * IDLE.arm * k;   // 相位落後呼吸 0.9 rad
  return o;
}
